import {useEffect, useMemo, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBinoculars, faCalendarDays, faCamera, faLocationDot, faRoute, faSeedling, faStar} from "@fortawesome/free-solid-svg-icons";
import {Link, useNavigate} from "react-router-dom";
import {ExplorerHero, ExplorerHeroStat, ExplorerHeroStats} from "../../../components/explorer-hero";
import CalculationLoading from "../../../components/calculation-loading";
import DataChart from "../../../components/data-chart";
import {getRandomDashboardHeroImage} from "../../../helpers/dashboardHeroImages";
import {buildBirdingPersonalityProfile} from "../../../helpers/birding-personality/helpers";
import type {BirdingPersonalityProfile} from "../../../helpers/birding-personality/types";
import {EARLY_PROFILE_INTRO, EARLY_PROFILE_TITLE, MONTH_LABELS} from "./constants";
import type {BirdingPersonalityViewProps} from "./types";
import './birding-personality.scss';

export default function BirdingPersonalityView({rawData, lifeList, checklist, speciesImages, homeLocation}: BirdingPersonalityViewProps) {
    const navigate = useNavigate();
    const heroImage = useMemo(() => getRandomDashboardHeroImage(), []);
    const [profile, setProfile] = useState<BirdingPersonalityProfile>();

    useEffect(() => {
        let isActive = true;
        setProfile(undefined);
        const timeoutId = window.setTimeout(() => {
            const nextProfile = buildBirdingPersonalityProfile({rawData, lifeList, checklist, speciesImages, homeLocation});
            if (isActive) setProfile(nextProfile);
        }, 0);

        return () => {
            isActive = false;
            window.clearTimeout(timeoutId);
        };
    }, [rawData, lifeList, checklist, speciesImages, homeLocation]);

    if (!profile) {
        return (
            <div className="discover birding-personality">
                <CalculationLoading
                    title="Reading your birding patterns"
                    message="Reviewing your outings, places, seasons, and species to build your profile."
                />
            </div>
        );
    }

    const title = profile.primary?.label ?? EARLY_PROFILE_TITLE;
    const intro = profile.primary?.description ?? EARLY_PROFILE_INTRO;

    return (
        <div className="discover birding-personality">
            <ExplorerHero
                backgroundImage={heroImage}
                eyebrow="Birding Personality"
                title={title}
                intro={intro}
                backLabel="Discover"
                onBack={() => navigate('/discover')}
            >
                <ExplorerHeroStats ariaLabel="Birding personality summary">
                    <ExplorerHeroStat icon={<FontAwesomeIcon icon={faBinoculars} />} label="Species Seen" value={profile.stats.speciesCount.toLocaleString()} />
                    <ExplorerHeroStat icon={<FontAwesomeIcon icon={faRoute} />} label="Checklists" value={profile.stats.checklistCount.toLocaleString()} />
                    <ExplorerHeroStat icon={<FontAwesomeIcon icon={faCalendarDays} />} label="Years Birding" value={profile.stats.activeYears.toLocaleString()} />
                </ExplorerHeroStats>
            </ExplorerHero>

            <div className="birding-personality__layout">
                <main className="birding-personality__main">
                    <section className="birding-personality__panel" aria-labelledby="personality-style-title">
                        <p className="birding-personality__eyebrow">{profile.isEarlyProfile ? 'Early signs' : 'Your birding style'}</p>
                        <h2 id="personality-style-title">{profile.isEarlyProfile ? 'What is already showing through' : 'The patterns that stand out'}</h2>
                        <div className="birding-personality__signal-list">
                            {(profile.isEarlyProfile ? profile.directionalSigns : profile.traits).map(signal => (
                                <article className="birding-personality__signal" key={signal.id}>
                                    <FontAwesomeIcon icon={faSeedling} aria-hidden="true" />
                                    <div><h3>{signal.label}</h3><p>{signal.description}</p></div>
                                </article>
                            ))}
                            {!profile.directionalSigns.length ? <p className="birding-personality__empty">Add a few more checklists to reveal your first directional signs.</p> : null}
                        </div>
                    </section>

                    <section className="birding-personality__panel" aria-labelledby="personality-facts-title">
                        <p className="birding-personality__eyebrow">Fun facts</p>
                        <h2 id="personality-facts-title">A few things about your birding</h2>
                        <div className="birding-personality__facts">
                            <div><FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" /><span>Most active day</span><strong>{profile.stats.mostActiveWeekday ?? 'Still emerging'}</strong></div>
                            <div><FontAwesomeIcon icon={faCamera} aria-hidden="true" /><span>Photo outings</span><strong>{profile.stats.photoOutingPercent}%</strong></div>
                            <div><FontAwesomeIcon icon={faLocationDot} aria-hidden="true" /><span>Places explored</span><strong>{profile.stats.locationCount.toLocaleString()}</strong></div>
                            {profile.funFacts.map(fact => <p key={fact}>{fact}</p>)}
                        </div>
                    </section>
                    <section className="birding-personality__chart-grid" aria-label="Birding patterns">
                        <article className="birding-personality__panel">
                            <p className="birding-personality__eyebrow">When you bird</p>
                            <h2>Checklists by month</h2>
                            <DataChart ariaLabel="Checklist count by month" mode="vertical" items={profile.monthlyChecklistCounts.map(item => ({label: MONTH_LABELS[item.month - 1], value: item.checklistCount}))} />
                        </article>
                        <article className="birding-personality__panel">
                            <p className="birding-personality__eyebrow">Your yearly pattern</p>
                            <h2>Species by year</h2>
                            <DataChart ariaLabel="Unique species observed by year" mode="vertical" items={profile.yearlySpeciesCounts.map(item => ({label: item.year.toString(), value: item.speciesCount}))} />
                        </article>
                    </section>
                </main>

                <aside className="birding-personality__aside">
                    <section className="birding-personality__panel" aria-labelledby="personality-affinities-title">
                        <p className="birding-personality__eyebrow">Your affinities</p>
                        <h2 id="personality-affinities-title">The birds you return to</h2>
                        {profile.affinities.length ? (
                            <div className="birding-personality__affinities">
                                {profile.affinities.map(affinity => (
                                    <article key={affinity.id}>
                                        <FontAwesomeIcon icon={faStar} aria-hidden="true" />
                                        <div><h3>{affinity.label}</h3><p>{affinity.description}</p><span>{affinity.speciesCount.toLocaleString()} species across {affinity.outingCount.toLocaleString()} outings</span></div>
                                    </article>
                                ))}
                            </div>
                        ) : <p className="birding-personality__empty">As your record grows, recurring bird groups will appear here.</p>}
                    </section>
                    <section className="birding-personality__panel birding-personality__place-panel" aria-labelledby="personality-places-title">
                        <p className="birding-personality__eyebrow">Your reach</p>
                        <h2 id="personality-places-title">Where your birding has taken you</h2>
                        <dl>
                            <div><dt>Counties</dt><dd>{profile.stats.countyCount.toLocaleString()}</dd></div>
                            <div><dt>States / Provinces</dt><dd>{profile.stats.stateProvinceCount.toLocaleString()}</dd></div>
                            {profile.stats.homeChecklistPercent !== undefined ? <div><dt>Near home</dt><dd>{profile.stats.homeChecklistPercent}%</dd></div> : null}
                        </dl>
                    </section>
                    <section className="birding-personality__panel" aria-labelledby="personality-groups-title">
                        <p className="birding-personality__eyebrow">Top bird groups</p>
                        <h2 id="personality-groups-title">By number of species</h2>
                        <DataChart ariaLabel="Top bird families by number of species" mode="horizontal" items={profile.topFamilyCounts.map(item => ({label: item.label, value: item.count}))} valueLabel={value => `${value.toLocaleString()} species`} />
                    </section>
                </aside>
            </div>
            <section className="birding-personality__panel birding-personality__signature-species" aria-labelledby="personality-signature-species-title">
                <p className="birding-personality__eyebrow">Signature species</p>
                <h2 id="personality-signature-species-title">Birds that stand out in your record</h2>
                <div className="birding-personality__signature-grid">
                    {profile.signatureSpecies.map(species => {
                        const content = <>
                            {species.image ? <img src={species.image} alt="" /> : <span className="birding-personality__signature-placeholder" aria-hidden="true"><FontAwesomeIcon icon={faBinoculars} /></span>}
                            <strong>{species.label}</strong>
                            <span>{species.observationCount.toLocaleString()} records{species.photoCount ? ` · ${species.photoCount.toLocaleString()} photos` : ''}</span>
                        </>;
                        return species.detailPath ? <Link to={species.detailPath} key={species.speciesKey}>{content}</Link> : <article key={species.speciesKey}>{content}</article>;
                    })}
                </div>
            </section>
        </div>
    );
}
